import { useCallback, useMemo } from "react"
import { Address } from "viem"
import { useReadContract } from "wagmi"

import { ONE_SECOND_IN_MILLISECONDS } from "#/shared/constants"
import { SwappableToken } from "#/shared/types"
import { getMezoTokenAddress } from "#/shared/utils"

import useSwapPool from "./useSwapPool"

const refetchInterval = ONE_SECOND_IN_MILLISECONDS * 30 // 30 seconds

const poolAbi = [
  {
    inputs: [],
    name: "getReserves",
    outputs: [
      {
        internalType: "uint256",
        name: "_reserve0",
        type: "uint256",
      },
      {
        internalType: "uint256",
        name: "_reserve1",
        type: "uint256",
      },
      {
        internalType: "uint256",
        name: "_blockTimestampLast",
        type: "uint256",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "tokens",
    outputs: [
      {
        internalType: "address",
        name: "",
        type: "address",
      },
      {
        internalType: "address",
        name: "",
        type: "address",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
]

const useSwapReserves = (tokenIn: SwappableToken, tokenOut: SwappableToken) => {
  const { data: swapPool } = useSwapPool(tokenIn, tokenOut)

  const { data: swapReservesData } = useReadContract({
    abi: poolAbi,
    address: swapPool as Address,
    functionName: "getReserves",
    query: { enabled: !!swapPool, refetchInterval, staleTime: refetchInterval },
  })

  const { data: swapPoolTokensData } = useReadContract({
    abi: poolAbi,
    address: swapPool as Address,
    functionName: "tokens",
    query: { enabled: !!swapPool, refetchInterval, staleTime: refetchInterval },
  })

  const swapReserves = swapReservesData as [bigint, bigint, bigint]
  const swapPoolTokens = swapPoolTokensData as [Address, Address]

  const isSwapPoolEmpty = useMemo(
    () =>
      swapReserves ? swapReserves[0] === 0n || swapReserves[1] === 0n : true,
    [swapReserves],
  )

  const hasPoolEnoughLiquidity = useCallback(
    (tokenInAmount: bigint, tokenOutAmount: bigint) => {
      if (!swapPoolTokens || !swapReserves) return false

      const tokenInAddress = getMezoTokenAddress(tokenIn)
      const tokenOutAddress = getMezoTokenAddress(tokenOut)

      if (!tokenInAddress || !tokenOutAddress) return false

      // We are checking for tokens indexes in the pool as we can swap in both ways
      const tokenInPoolReservesIndex = swapPoolTokens.indexOf(tokenInAddress)
      const tokenOutPoolReservesIndex = swapPoolTokens.indexOf(tokenOutAddress)

      if (tokenInPoolReservesIndex < 0 || tokenOutPoolReservesIndex < 0) {
        return false
      }

      return (
        swapReserves[tokenInPoolReservesIndex] >= tokenInAmount &&
        swapReserves[tokenOutPoolReservesIndex] >= tokenOutAmount
      )
    },
    [swapPoolTokens, tokenIn, tokenOut, swapReserves],
  )

  return { isSwapPoolEmpty, hasPoolEnoughLiquidity }
}

export default useSwapReserves
